import React, { Component } from "react";
import { connect } from "react-redux";
import { Container, Row, Col, Button, Form, Table, Spinner, InputGroup, Badge, Modal } from "react-bootstrap";
import { FaEye, FaEdit, FaTrash, FaPlus, FaCopy } from 'react-icons/fa';

import moment from 'moment';
import { getCompanies, deleteCompany } from '../../actions/companyAction';
import { getTranslate, LANGUAGE_KEYS } from '../../utils/language';
import CompanyView from './View';
import CompanyEdit from './Edit';

class CompanyList extends Component {
  state = {
    loading: false,
    currentPage: 1,
    itemsPerPage: 20,
    sortBy: 'created_at',
    sortOrder: -1,
    keyword: '',
    typeFilter: 'all', // all, seller, factory, shipping_agency, system
    showViewModal: false,
    showEditModal: false,
    selectedCompanyId: null
  };

  componentDidMount() {
    this.loadCompanies();
  }

  loadCompanies = () => {
    this.setState({ loading: true });
    
    const query = {
      currentPage: this.state.currentPage,
      itemsPerPage: this.state.itemsPerPage,
      sortBy: this.state.sortBy,
      sortOrder: this.state.sortOrder,
      keyword: this.state.keyword
    };

    // Add type filter
    if (this.state.typeFilter !== 'all') {
      query[this.state.typeFilter] = true;
    }

    this.props.getCompanies(query)
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  handlePageChange = (page) => {
    this.setState({ currentPage: page }, this.loadCompanies);
  };

  handleSort = (field) => {
    this.setState(prevState => ({
      sortBy: field,
      sortOrder: prevState.sortBy === field ? -prevState.sortOrder : -1
    }), this.loadCompanies);
  };

  handleSearch = (e) => {
    this.setState({ keyword: e.target.value, currentPage: 1 }, this.loadCompanies);
  };

  handleTypeFilterChange = (e) => {
    this.setState({ typeFilter: e.target.value, currentPage: 1 }, this.loadCompanies);
  };

  handleDelete = async (company) => {
    if (window.confirm(`Bạn có chắc chắn muốn xóa công ty "${company.name}"?`)) {
      try {
        await this.props.deleteCompany(company._id);
        alert('Xóa công ty thành công!');
        this.loadCompanies();
      } catch (error) {
        alert('Lỗi khi xóa công ty: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  handleViewCompany = (companyId) => {
    this.setState({
      selectedCompanyId: companyId,
      showViewModal: true
    });
  };

  handleCreateCompany = () => {
    this.setState({
      showEditModal: true,
      selectedCompanyId: null // null means create new
    });
  };

  handleEditCompany = (companyId) => {
    this.setState({
      selectedCompanyId: companyId,
      showEditModal: true
    });
  };

  handleCloseViewModal = () => {
    this.setState({
      showViewModal: false,
      selectedCompanyId: null
    });
  };

  handleCloseEditModal = () => {
    this.setState({
      showEditModal: false,
      selectedCompanyId: null
    });
    // Reload companies sau khi edit
    this.loadCompanies();
  };

  getCompanyTypesBadges = (company) => {
    const types = [];
    if (company.is_seller) types.push({ text: 'Seller', variant: 'primary' });
    if (company.is_factory) types.push({ text: 'Factory', variant: 'success' });
    if (company.is_shipping_agency) types.push({ text: 'Shipping', variant: 'info' });
    if (company.is_system) types.push({ text: 'System', variant: 'warning' });
    
    return types.map((type, index) => (
      <Badge key={index} bg={type.variant} className="me-1">
        {type.text}
      </Badge>
    ));
  };

  render() {
    const { companies = [], totalCount = 0 } = this.props;
    const { loading, currentPage, itemsPerPage, sortBy, sortOrder, keyword, typeFilter, showViewModal, showEditModal, selectedCompanyId } = this.state;
    const totalPages = Math.ceil(totalCount / itemsPerPage);

    return (
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <h2>Quản lý Công ty</h2>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={this.handleCreateCompany}>
              <FaPlus /> Thêm công ty mới
            </Button>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={3}>
            <Form.Select value={typeFilter} onChange={this.handleTypeFilterChange}>
              <option value="all">Tất cả loại</option>
              <option value="is_seller">Seller</option>
              <option value="is_factory">Factory</option>
              <option value="is_shipping_agency">Shipping Agency</option>
              <option value="is_system">System</option>
            </Form.Select>
          </Col>
          <Col md={9}>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Tìm kiếm theo tên công ty, company_id..."
                value={keyword}
                onChange={this.handleSearch}
              />
            </InputGroup>
          </Col>
        </Row>

        {loading && (
          <div className="text-center mb-3">
            <Spinner animation="border" variant="primary" />
            <span className="ms-2">Đang tải...</span>
          </div>
        )}

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Logo</th>
              <th onClick={() => this.handleSort('company_id')} style={{ cursor: 'pointer' }}>
                ID {sortBy === 'company_id' && (sortOrder < 0 ? '↓' : '↑')}
              </th>
              <th onClick={() => this.handleSort('name')} style={{ cursor: 'pointer' }}>
                Tên {sortBy === 'name' && (sortOrder < 0 ? '↓' : '↑')}
              </th>
              <th onClick={() => this.handleSort('company_shortcode')} style={{ cursor: 'pointer' }}>
                Mã {sortBy === 'company_shortcode' && (sortOrder < 0 ? '↓' : '↑')}
              </th>
              <th>Mô tả</th>
              <th>Loại</th>
              <th>Liên hệ</th>
              <th onClick={() => this.handleSort('created_at')} style={{ cursor: 'pointer' }}>
                Ngày tạo {sortBy === 'created_at' && (sortOrder < 0 ? '↓' : '↑')}
              </th>
              <th>Hành động</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((company) => (
              <tr key={company._id}>
                <td style={{ width: '80px', textAlign: 'center' }}>
                  {company.logo ? (
                    <img 
                      src={company.logo} 
                      alt={`Logo ${company.name}`}
                      style={{ 
                        height: '80px',
                        width: 'auto',
                        maxWidth: '120px',
                        objectFit: 'contain',
                        borderRadius: '4px',
                        border: '1px solid #dee2e6'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : (
                    <div 
                      style={{ 
                        width: '80px', 
                        height: '80px', 
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: '#6c757d',
                        margin: '0 auto'
                      }}
                    >
                      No Logo
                    </div>
                  )}
                  {company.logo && (
                    <div 
                      style={{ 
                        width: '60px', 
                        height: '60px', 
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        display: 'none',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: '#6c757d',
                        margin: '0 auto'
                      }}
                    >
                      Error
                    </div>
                  )}
                </td>
                <td className="fw-bold">{company.company_id}</td>
                <td>
                  <div className="fw-bold">{company.name}</div>
                  {company.legal_name && (
                    <small className="text-muted d-block">{company.legal_name}</small>
                  )}
                </td>
                <td>
                  <div className="d-flex align-items-center">
                    <code 
                      className="bg-light px-2 py-1 rounded text-primary fw-bold me-2" 
                      style={{ 
                        fontSize: '0.85em',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => {
                        window.open(`https://${company.company_shortcode.toLowerCase()}.TKT1.ai`, '_blank');
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#007bff';
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '';
                        e.target.style.color = '';
                      }}
                      title={`Click để mở ${company.company_shortcode.toLowerCase()}.TKT1.ai`}
                    >
                      {company.company_shortcode}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`https://${company.company_shortcode.toLowerCase()}.TKT1.ai`);
                        // Show temporary feedback
                        const btn = event.target.closest('button');
                        const originalContent = btn.innerHTML;
                        btn.innerHTML = '<span style="color: green;">✓</span>';
                        setTimeout(() => { btn.innerHTML = originalContent; }, 1000);
                      }}
                      className="btn btn-sm btn-outline-secondary"
                      style={{ 
                        padding: '2px 6px',
                        fontSize: '0.7rem',
                        border: 'none'
                      }}
                      title={`Copy https://${company.company_shortcode.toLowerCase()}.TKT1.ai`}
                    >
                      <FaCopy />
                    </button>
                  </div>
                </td>
                <td style={{ maxWidth: '250px' }}>
                  {company.description ? (
                    <div style={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap'
                    }} title={company.description}>
                      {company.description}
                    </div>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td>
                  {this.getCompanyTypesBadges(company)}
                </td>
                <td>
                  {company.tel && <div><small>Tel: {company.tel}</small></div>}
                  {company.website && <div><small>Web: {company.website}</small></div>}
                </td>
                <td>{moment(company.created_at).format('DD/MM/YYYY')}</td>
                <td>
                  <Button 
                    variant="info" 
                    size="sm" 
                    onClick={() => this.handleViewCompany(company._id)}
                    className="me-1"
                    title="Xem chi tiết"
                  >
                    <FaEye />
                  </Button>
                  <Button 
                    variant="warning" 
                    size="sm" 
                    onClick={() => this.handleEditCompany(company._id)}
                    className="me-1"
                    title="Chỉnh sửa"
                  >
                    <FaEdit />
                  </Button>
                  <Button 
                    variant="danger" 
                    size="sm" 
                    onClick={() => this.handleDelete(company)}
                    title="Xóa"
                  >
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {companies.length === 0 && !loading && (
          <div className="text-center py-4">
            <p className="text-muted">Không tìm thấy công ty nào.</p>
          </div>
        )}

        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-3">
            <Button
              variant="outline-primary"
              className="me-2"
              disabled={currentPage === 1}
              onClick={() => this.handlePageChange(currentPage - 1)}
            >
              Trang trước
            </Button>
            <span className="mx-2 my-auto">
              Trang {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline-primary"
              className="ms-2"
              disabled={currentPage === totalPages}
              onClick={() => this.handlePageChange(currentPage + 1)}
            >
              Trang sau
            </Button>
          </div>
        )}

        {/* View Modal */}
        <Modal show={showViewModal} onHide={this.handleCloseViewModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Chi tiết công ty</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedCompanyId && (
              <CompanyView
                match={{ params: { id: selectedCompanyId } }}
                history={{
                  push: () => {
                    // Block URL changes in modal mode
                    console.log('URL navigation blocked in modal mode');
                  },
                  replace: () => {
                    // Block URL changes in modal mode
                    console.log('URL replace blocked in modal mode');
                  },
                  go: () => {
                    // Block navigation
                    console.log('Navigation blocked in modal mode');
                  },
                  goBack: () => {
                    // Block navigation
                    console.log('Navigation blocked in modal mode');
                  },
                  goForward: () => {
                    // Block navigation
                    console.log('Navigation blocked in modal mode');
                  },
                  listen: () => () => {}, // Return empty unsubscribe function
                  block: () => () => {}, // Return empty unsubscribe function
                  location: window.location,
                  length: 0
                }}
                onHide={this.handleCloseViewModal}
              />
            )}
          </Modal.Body>
        </Modal>

        {/* Edit Modal */}
        <Modal show={showEditModal} onHide={this.handleCloseEditModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedCompanyId ? 'Chỉnh sửa công ty' : 'Thêm công ty mới'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <CompanyEdit
              match={{ 
                params: { id: selectedCompanyId || 'create' }
              }}
              history={{
                push: () => {
                  // Block URL changes in modal mode
                  console.log('URL navigation blocked in modal mode');
                },
                replace: () => {
                  // Block URL changes in modal mode
                  console.log('URL replace blocked in modal mode');
                },
                go: () => {
                  // Block navigation
                  console.log('Navigation blocked in modal mode');
                },
                goBack: () => {
                  // Block navigation
                  console.log('Navigation blocked in modal mode');
                },
                goForward: () => {
                  // Block navigation
                  console.log('Navigation blocked in modal mode');
                },
                listen: () => () => {}, // Return empty unsubscribe function
                block: () => () => {}, // Return empty unsubscribe function
                location: window.location,
                length: 0
              }}
              companyId={selectedCompanyId}
              isModal={true}
              onHide={this.handleCloseEditModal}
            />
          </Modal.Body>
        </Modal>
      </Container>
    );
  }
}

const mapStateToProps = (state) => ({
  companies: state.companyReducer.companies,
  totalCount: state.companyReducer.totalCount,
  loading: state.companyReducer.loading,
  error: state.companyReducer.error
});

export default connect(mapStateToProps, {
  getCompanies,
  deleteCompany
})(CompanyList);
